<template>
  <nav
    ref="nav"
    class="mobile-bottom-nav"
    :aria-label="tt('nav.mobileBottomNav', 'Primary navigation')"
    @scroll.passive="onNavScroll"
  >
    <span
      class="mobile-bottom-nav__indicator"
      :class="{ 'mobile-bottom-nav__indicator--ready': indicatorReady }"
      :style="indicatorStyle"
      aria-hidden="true"
    ></span>
    <template v-for="tab in tabs">
      <button
        v-if="isProfileTab(tab)"
        :key="`${tab.path}-profile`"
        type="button"
        class="mobile-bottom-nav__tab mobile-bottom-nav__tab--button"
        :class="{ 'mobile-bottom-nav__tab--active': isTabActive(tab) }"
        :aria-current="isTabActive(tab) ? 'page' : undefined"
        :aria-expanded="profileMenuOpen ? 'true' : 'false'"
        aria-haspopup="dialog"
        @click="onProfileTabClick"
      >
        <span class="mobile-bottom-nav__icon-wrap">
          <i
            v-if="tab.icon"
            :class="tab.icon"
            class="mobile-bottom-nav__icon"
            aria-hidden="true"
          ></i>
          <span
            v-if="tabTag(tab)"
            class="mobile-bottom-nav__tag"
            :class="{
              'mobile-bottom-nav__tag--soon': tab.tag === 'comingSoon',
            }"
          >
            {{ tabTag(tab) }}
          </span>
        </span>
        <span class="mobile-bottom-nav__label">{{ tabLabel(tab) }}</span>
      </button>
      <button
        v-else-if="isAdminTab(tab)"
        :key="`${tab.path}-admin`"
        ref="adminTrigger"
        type="button"
        class="mobile-bottom-nav__tab mobile-bottom-nav__tab--button"
        :class="{ 'mobile-bottom-nav__tab--active': isTabActive(tab) }"
        :aria-current="isAdminRouteActive ? 'page' : undefined"
        :aria-expanded="adminMenuOpen ? 'true' : 'false'"
        aria-haspopup="menu"
        aria-controls="mobile-bottom-nav-admin-menu"
        @click="onAdminTabClick"
      >
        <span class="mobile-bottom-nav__icon-wrap">
          <i
            :class="tab.icon"
            class="mobile-bottom-nav__icon"
            aria-hidden="true"
          ></i>
        </span>
        <span class="mobile-bottom-nav__label">{{ tabLabel(tab) }}</span>
      </button>
      <router-link
        v-else
        :key="tab.path"
        :to="tab.path"
        class="mobile-bottom-nav__tab"
        :class="{
          'mobile-bottom-nav__tab--active': isTabActive(tab),
          'mobile-bottom-nav__tab--clips': tab.path === '/create-clips',
        }"
        :aria-current="isTabActive(tab) ? 'page' : undefined"
        @click.native="onTabClick(tab)"
      >
        <span class="mobile-bottom-nav__icon-wrap">
          <i
            v-if="tab.icon"
            :class="tab.icon"
            class="mobile-bottom-nav__icon"
            aria-hidden="true"
          ></i>
          <span
            v-if="tabTag(tab)"
            class="mobile-bottom-nav__tag"
            :class="{
              'mobile-bottom-nav__tag--soon': tab.tag === 'comingSoon',
            }"
          >
            {{ tabTag(tab) }}
          </span>
        </span>
        <span class="mobile-bottom-nav__label">{{ tabLabel(tab) }}</span>
      </router-link>
    </template>
    <body-portal v-if="adminMenuOpen">
      <ul
        id="mobile-bottom-nav-admin-menu"
        class="mobile-bottom-nav__admin-menu"
        :style="adminMenuStyle"
        role="menu"
        :aria-label="tt('nav.admin', 'Admin')"
      >
        <li v-for="route in adminRoutes" :key="route.path" role="none">
          <router-link
            :to="route.path"
            class="mobile-bottom-nav__admin-menu-item"
            :class="{ 'mobile-bottom-nav__admin-menu-item--active': isPathActive(route.path) }"
            role="menuitem"
            @click.native="onAdminRouteClick(route)"
          >
            <i :class="route.icon" aria-hidden="true"></i>
            <span>{{ route.name }}</span>
          </router-link>
        </li>
      </ul>
    </body-portal>
  </nav>
</template>

<script>
import { mapState } from "pinia";
import {
  getNavRoutes,
  getMobileBottomNavTabs,
  MOBILE_PROFILE_MENU_OPEN_EVENT,
  PROFILE_MENU_STATE_EVENT,
} from "@/config/navRoutes";
import { useCurrentUserStore } from "@/stores/currentUser";

export default {
  name: "MobileBottomNav",
  data() {
    return {
      profileMenuOpen: false,
      adminMenuOpen: false,
      adminMenuStyle: {},
      indicatorReady: false,
      indicatorStyle: {
        width: "0px",
        height: "0px",
        transform: "translate3d(0, 0, 0)",
        opacity: "0",
      },
    };
  },
  computed: {
    ...mapState(useCurrentUserStore, ["isTwitchUser", "hasAdminAccess"]),
    adminRoutes() {
      if (!this.hasAdminAccess) return [];
      return getNavRoutes(this.isTwitchUser).filter((route) => route.adminOnly);
    },
    isAdminRouteActive() {
      return this.adminRoutes.some((route) => this.isPathActive(route.path));
    },
    tabs() {
      const locale = this.$i18n?.locale;
      void locale;
      const tabs = getMobileBottomNavTabs(this.isTwitchUser);
      if (!this.hasAdminAccess) return tabs;
      return [
        ...tabs,
        {
          path: "__admin__",
          name: this.tt("nav.admin", "Admin"),
          icon: "fa-solid fa-user-shield",
          adminMenu: true,
        },
      ];
    },
    currentPath() {
      return String(this.$route?.path || "").replace(/\/$/, "") || "/";
    },
    activeTabIndex() {
      return this.tabs.findIndex((tab) => this.isTabActive(tab));
    },
  },
  watch: {
    activeTabIndex() {
      this.scheduleIndicatorUpdate();
    },
    tabs() {
      this.scheduleIndicatorUpdate();
    },
    profileMenuOpen() {
      this.scheduleIndicatorUpdate();
    },
    adminMenuOpen() {
      this.scheduleIndicatorUpdate();
    },
  },
  mounted() {
    window.addEventListener(PROFILE_MENU_STATE_EVENT, this.onProfileMenuState);
    window.addEventListener("resize", this.scheduleIndicatorUpdate);
    this.scheduleIndicatorUpdate();
  },
  beforeDestroy() {
    this.closeAdminMenu();
    window.removeEventListener(PROFILE_MENU_STATE_EVENT, this.onProfileMenuState);
    window.removeEventListener("resize", this.scheduleIndicatorUpdate);
    if (this._indicatorRaf) {
      cancelAnimationFrame(this._indicatorRaf);
    }
  },
  methods: {
    tt(key, fallback) {
      if (typeof this.$t !== "function") return fallback;
      const translated = this.$t(key);
      return typeof translated === "string" && translated !== key ? translated : fallback;
    },
    tabLabel(tab) {
      if (tab.shortNameKey) {
        return this.tt(tab.shortNameKey, tab.shortNameFallback || tab.name);
      }
      return tab.name;
    },
    tabTag(tab) {
      if (tab.tag === "comingSoon") {
        return this.tt("nav.comingSoonTag", "Próximamente");
      }
      return null;
    },
    isProfileTab(tab) {
      return tab.path === "/user-profile" || tab.path === "/stats";
    },
    isAdminTab(tab) {
      return tab.adminMenu === true;
    },
    isPathActive(path) {
      const normalized = String(path || "").replace(/\/$/, "") || "/";
      return this.currentPath === normalized || this.currentPath.startsWith(`${normalized}/`);
    },
    isTabActive(tab) {
      const path = this.currentPath;
      if (this.isAdminTab(tab)) {
        return this.adminMenuOpen || this.isAdminRouteActive;
      }
      if (this.isProfileTab(tab)) {
        return this.profileMenuOpen || path === tab.path || path.startsWith(`${tab.path}/`);
      }
      if (tab.path === "/create-clips") {
        return path === "/create-clips" || path.startsWith("/create-clips/");
      }
      return path === tab.path || path.startsWith(`${tab.path}/`);
    },
    onProfileMenuState(event) {
      this.profileMenuOpen = Boolean(event?.detail?.open);
      if (this.profileMenuOpen) {
        this.closeAdminMenu();
      }
    },
    onProfileTabClick() {
      this.closeAdminMenu();
      if (this.$sidebar?.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(MOBILE_PROFILE_MENU_OPEN_EVENT));
    },
    onTabClick(tab) {
      this.closeAdminMenu();
      if (this.$sidebar?.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
      if (!tab.sameRouteResetEvent) return;
      if (!this.isTabActive(tab)) return;
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(tab.sameRouteResetEvent));
    },
    getAdminTrigger() {
      const trigger = this.$refs.adminTrigger;
      return Array.isArray(trigger) ? trigger[0] : trigger;
    },
    getAdminMenu() {
      if (typeof document === "undefined") return null;
      return document.getElementById("mobile-bottom-nav-admin-menu");
    },
    onAdminTabClick() {
      if (this.profileMenuOpen && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(MOBILE_PROFILE_MENU_OPEN_EVENT));
      }
      if (this.adminMenuOpen) {
        this.closeAdminMenu();
        return;
      }
      this.adminMenuOpen = true;
      this.$nextTick(() => {
        this.syncAdminMenuPosition();
        window.addEventListener("pointerdown", this.onAdminOutsidePointerDown, true);
        window.addEventListener("keydown", this.onAdminEscapeKey);
        window.addEventListener("resize", this.syncAdminMenuPosition);
      });
    },
    closeAdminMenu() {
      this.adminMenuOpen = false;
      this.adminMenuStyle = {};
      window.removeEventListener("pointerdown", this.onAdminOutsidePointerDown, true);
      window.removeEventListener("keydown", this.onAdminEscapeKey);
      window.removeEventListener("resize", this.syncAdminMenuPosition);
    },
    onAdminOutsidePointerDown(event) {
      if (!this.adminMenuOpen) return;
      const trigger = this.getAdminTrigger();
      const menu = this.getAdminMenu();
      if (trigger?.contains(event.target) || menu?.contains(event.target)) return;
      this.closeAdminMenu();
    },
    onAdminEscapeKey(event) {
      if (!this.adminMenuOpen || event.key !== "Escape") return;
      event.preventDefault();
      this.closeAdminMenu();
    },
    syncAdminMenuPosition() {
      if (!this.adminMenuOpen) return;
      const trigger = this.getAdminTrigger();
      const menu = this.getAdminMenu();
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuWidth = menu.offsetWidth;
      const viewportPadding = 12;
      const left = Math.min(
        Math.max(viewportPadding, triggerRect.left + triggerRect.width / 2 - menuWidth / 2),
        window.innerWidth - menuWidth - viewportPadding
      );

      this.adminMenuStyle = {
        position: "fixed",
        left: `${left}px`,
        bottom: `${window.innerHeight - triggerRect.top + 8}px`,
        zIndex: 1060,
      };
    },
    onAdminRouteClick(route) {
      this.closeAdminMenu();
      if (this.$sidebar?.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
      if (!route.sameRouteResetEvent || typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(route.sameRouteResetEvent));
    },
    onNavScroll() {
      this.scheduleIndicatorUpdate();
      this.syncAdminMenuPosition();
    },
    scheduleIndicatorUpdate() {
      if (this._indicatorRaf) {
        cancelAnimationFrame(this._indicatorRaf);
      }
      this._indicatorRaf = requestAnimationFrame(() => {
        this._indicatorRaf = null;
        this.$nextTick(() => {
          this.updateIndicator();
        });
      });
    },
    updateIndicator() {
      const nav = this.$refs.nav;
      if (!nav) return;

      const tabEls = nav.querySelectorAll(".mobile-bottom-nav__tab");
      const index = this.activeTabIndex;
      const tab = index >= 0 ? tabEls[index] : null;

      if (!tab) {
        this.indicatorStyle = {
          ...this.indicatorStyle,
          opacity: "0",
        };
        return;
      }

      const left = tab.offsetLeft;
      const width = tab.offsetWidth;
      const top = 0;
      const height = nav.clientHeight;

      this.indicatorStyle = {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate3d(${left}px, ${top}px, 0)`,
        opacity: "1",
      };

      if (!this.indicatorReady) {
        // Enable transitions only after the first position is set (avoids slide-from-zero).
        requestAnimationFrame(() => {
          this.indicatorReady = true;
        });
      }
    },
  },
};
</script>

<style scoped>
.mobile-bottom-nav {
  display: none;
  position: relative;
  left: auto;
  right: auto;
  bottom: auto;
  z-index: 1040;
  flex: 0 0 auto;
  width: 100%;
  box-sizing: border-box;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 0;
  min-height: 64px;
  padding: 0;
  border-top: 1px solid var(--clipr-border-color);
  background: rgba(15, 15, 24, 0.97);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.55);
  overflow: visible;
}

@media (max-width: 991px) {
  .mobile-bottom-nav {
    display: flex;
  }
}

.mobile-bottom-nav__indicator {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  box-sizing: border-box;
  border: 2px solid #c084fc;
  border-radius: 6px;
  background: rgba(192, 132, 252, 0.1);
  pointer-events: none;
  will-change: transform, width, height;
}

.mobile-bottom-nav__indicator--ready {
  transition:
    transform 0.32s cubic-bezier(0.34, 1.15, 0.64, 1),
    width 0.32s cubic-bezier(0.34, 1.15, 0.64, 1),
    height 0.32s cubic-bezier(0.34, 1.15, 0.64, 1),
    opacity 0.2s ease;
}

.mobile-bottom-nav__tab {
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  align-self: stretch;
  min-width: 0;
  min-height: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  box-sizing: border-box;
  padding: 8px 2px 6px;
  text-decoration: none;
  color: #ffffff;
  font-weight: 500;
  transition: color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  overflow: visible;
}

.mobile-bottom-nav__tab--button {
  appearance: none;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
}

.mobile-bottom-nav__tab:hover,
.mobile-bottom-nav__tab:focus-visible {
  color: #ffffff;
  outline: none;
}

.mobile-bottom-nav__tab--active {
  color: #c084fc;
}

.mobile-bottom-nav__tab--active .mobile-bottom-nav__label {
  color: #c084fc;
  font-weight: 600;
}

.mobile-bottom-nav__icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 100%;
  height: 1.6rem;
  overflow: visible;
}

.mobile-bottom-nav__icon {
  font-size: 1.05rem;
  line-height: 1;
  transform: scale(0.92);
  transform-origin: center center;
  transition: transform 0.22s cubic-bezier(0.34, 1.3, 0.64, 1), color 0.15s ease;
}

.mobile-bottom-nav__tab--active .mobile-bottom-nav__icon {
  color: #c084fc;
  transform: scale(1.12);
}

.mobile-bottom-nav__tab--clips .mobile-bottom-nav__icon {
  font-size: 1.55rem;
}

.mobile-bottom-nav__tab--clips.mobile-bottom-nav__tab--active .mobile-bottom-nav__icon {
  transform: scale(1.08);
}

.mobile-bottom-nav__label {
  display: block;
  flex: 0 0 auto;
  width: 100%;
  max-width: 100%;
  height: 1.2em;
  padding: 0 1px;
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-bottom-nav__tag {
  position: absolute;
  z-index: 1;
  max-width: none;
  padding: 1px 5px;
  border-radius: 999px;
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: nowrap;
  pointer-events: none;
}

.mobile-bottom-nav__tag--soon {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-32deg);
  color: var(--clipr-citrus-light);
  border: 1px solid var(--clipr-citrus-border);
  background: rgba(15, 15, 24, 0.95);
}

.mobile-bottom-nav__admin-menu {
  width: min(280px, calc(100vw - 24px));
  max-height: calc(100dvh - 96px);
  margin: 0;
  padding: 6px;
  list-style: none;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-md);
  background: var(--clipr-bg-elevated);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(168, 85, 247, 0.12);
  overflow-y: auto;
}

.mobile-bottom-nav__admin-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: var(--clipr-radius-sm);
  color: var(--clipr-text-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}

.mobile-bottom-nav__admin-menu-item:hover,
.mobile-bottom-nav__admin-menu-item:focus-visible,
.mobile-bottom-nav__admin-menu-item--active {
  color: var(--clipr-text-primary);
  background: var(--clipr-purple-overlay-medium);
  outline: none;
}

.mobile-bottom-nav__admin-menu-item--active {
  box-shadow: inset 0 0 0 1px var(--clipr-border-purple);
}

@media (max-width: 991px) {
  .mobile-bottom-nav {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
  }

  .mobile-bottom-nav::-webkit-scrollbar {
    display: none;
  }

  .mobile-bottom-nav__tab {
    flex: 0 0 20%;
    min-width: 0;
  }
}

@media (max-width: 380px) {
  .mobile-bottom-nav__label {
    font-size: 0.58rem;
  }

  .mobile-bottom-nav__tag {
    font-size: 0.45rem;
    padding: 1px 4px;
  }
}
</style>
